import { useState, useEffect, useRef } from "react";

import { useNavigate, Link, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, CheckCircle, AlertCircle, Info } from "lucide-react";
import musikonzeptLogo from "@/assets/musikonzept-logo.png";
import { safeGetItem, safeSetItem, safeRemoveItem } from "@/lib/safeStorage";
import { registerSchema, passwordChecks } from "@/lib/schemas/registerSchema";
import { Check, X } from "lucide-react";
import { getAttribution, herkunftFuerAnmeldung } from "@/lib/marketing/attribution";
import { trackCompleteRegistration } from "@/lib/marketing/pixel";
import { istPasswortAusDatenleck, PASSWORT_DATENLECK } from "@/lib/passwortFehler";

const Registrieren = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isRegistrationInProgress, setIsRegistrationInProgress] = useState(false);
  const registrationInProgressRef = useRef(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationStatus, setMigrationStatus] = useState("");
  const [showAlreadyRegistered, setShowAlreadyRegistered] = useState(false);

  // Hydrate pending course from redirect URL if localStorage is empty
  useEffect(() => {
    const hydratePendingFromRedirect = async () => {
      const pendingCourseId = safeGetItem('pending_free_course_id');
      if (pendingCourseId) return; // Already have pending course

      const searchParams = new URLSearchParams(location.search);
      const redirectTo = searchParams.get('redirect');
      if (!redirectTo) return;

      // Check if redirect points to a course landing page
      const courseMatch = redirectTo.match(/\/(?:kurse?|kurs)\/([^/?]+)/);
      if (!courseMatch) return;

      const courseSlug = courseMatch[1];
      console.log('[Registrieren] Hydrating pending course from redirect:', courseSlug);

      try {
        const { data: course } = await supabase
          .from('courses')
          .select('id, slug, is_free')
          .eq('slug', courseSlug)
          .maybeSingle();

        if (course && course.is_free) {
          console.log('[Registrieren] Found free course, setting localStorage:', course);
          safeSetItem('pending_free_course_id', course.id);
          safeSetItem('pending_free_course_slug', course.slug);
        }
      } catch (e) {
        console.error('[Registrieren] Error hydrating course:', e);
      }
    };

    hydratePendingFromRedirect();
  }, [location.search]);

  useEffect(() => {
    const handlePendingEnrollmentAndRedirect = async (userId: string) => {
      // Check for pending free course enrollment
      const pendingCourseId = safeGetItem('pending_free_course_id');
      const pendingCourseSlug = safeGetItem('pending_free_course_slug');
      
      if (pendingCourseId && pendingCourseSlug) {
        try {
          const { data, error } = await supabase.functions.invoke("free-course-enroll", {
            body: { course_id: pendingCourseId }
          });
          
          // Clean up localStorage
          safeRemoveItem('pending_free_course_id');
          safeRemoveItem('pending_free_course_slug');
          
          if (!error && !data?.error) {
            // Ensure access + enrollments update immediately (avoid needing a manual refresh)
            await queryClient.invalidateQueries({ queryKey: ["global-access"] });
            await queryClient.invalidateQueries({ queryKey: ["course-enrollment"] });
            await queryClient.invalidateQueries({ queryKey: ["course-enrollment-check"] });
            await queryClient.invalidateQueries({ queryKey: ["student-courses"] });
            await queryClient.invalidateQueries({ queryKey: ["dashboard-courses"] });
            await queryClient.refetchQueries({ queryKey: ["global-access"] });

            toast.success("Erfolgreich eingeschrieben!", {
              description: "Du kannst jetzt mit dem Kurs starten.",
            });
            navigate(`/mein-bereich/kurs/${pendingCourseSlug}`);
            return true;
          }
        } catch (e) {
          console.error('Auto-enrollment failed:', e);
          safeRemoveItem('pending_free_course_id');
          safeRemoveItem('pending_free_course_slug');
        }
      }
      return false;
    };
    
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        const enrolled = await handlePendingEnrollmentAndRedirect(session.user.id);
        if (!enrolled) {
          navigate('/mein-bereich');
        }
      }
      setIsCheckingAuth(false);
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Skip if registration is in progress - handleRegister will handle everything
      if (registrationInProgressRef.current) {
        console.log('[Registrieren] Skipping onAuthStateChange - registration in progress');
        return;
      }
      
      if (event === 'SIGNED_IN' && session) {
        // Defer to avoid deadlock
        setTimeout(async () => {
          if (registrationInProgressRef.current) return; // Double-check after defer
          const enrolled = await handlePendingEnrollmentAndRedirect(session.user.id);
          if (!enrolled) {
            navigate('/mein-bereich');
          }
        }, 100);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, queryClient]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validation = registerSchema.safeParse({ 
      firstName, 
      lastName, 
      email, 
      password, 
      confirmPassword 
    });
    
    if (!validation.success) {
      const firstError = validation.error.errors[0];
      toast.error(firstError.message);
      return;
    }

    setIsLoading(true);
    setIsRegistrationInProgress(true);
    registrationInProgressRef.current = true; // Immediately visible to onAuthStateChange
    
    console.log('[Registrieren] Starting registration, pending course:', {
      pendingCourseId: safeGetItem('pending_free_course_id'),
      pendingCourseSlug: safeGetItem('pending_free_course_slug')
    });

    try {
      const pendingCourseSlug = safeGetItem('pending_free_course_slug');
      const redirectPath = pendingCourseSlug
        ? `/login?redirect=${encodeURIComponent(`/kurse/${pendingCourseSlug}`)}`
        : '/mein-bereich';
      const redirectUrl = `${window.location.origin}${redirectPath}`;
      
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            source: 'musikonzept',
            ...herkunftFuerAnmeldung(),
          },
        },
      });

      if (error) {
        if (error.message.includes("User already registered")) {
          setShowAlreadyRegistered(true);
        } else if (istPasswortAusDatenleck(error)) {
          toast.error(PASSWORT_DATENLECK);
        } else if (/password/i.test(error.message)) {
          toast.error("Passwort erfüllt die Anforderungen nicht", {
            description:
              "Bitte verwende mindestens 8 Zeichen mit einem Großbuchstaben, einem Kleinbuchstaben und einer Zahl.",
          });
        } else {
          toast.error("Registrierung fehlgeschlagen", {
            description: error.message,
          });
        }
        return;
      }

      if (data.user) {
        // Check if email confirmation is required
        if (data.user.identities && data.user.identities.length === 0) {
          setShowAlreadyRegistered(true);
        } else if (data.session) {
          // User is immediately signed in (email confirmation disabled)
          // Enroll user in Start-Bundle
          try {
            await supabase.from('bundle_enrollments').insert({
              user_id: data.user.id,
              bundle_id: 'a1b2c3d4-5678-90ab-cdef-1234567890ab', // Start-Zugang Bundle
            });
          } catch (enrollError) {
            console.error('Bundle enrollment error:', enrollError);
          }

          // Marketing-Attribution (UTM/fbclid/gclid/landing_path) auf das
          // Profil schreiben – non-blocking. Quelle: sessionStorage, das vom
          // globalen AttributionCapture beim ersten Seitenaufruf gefüllt wurde.
          // Das Profil hängt über `user_id` am Konto (`profiles.id` ist eine
          // eigene Nummer). Ohne Sitzung übernimmt der Trigger
          // `profiles_herkunft_aus_anmeldung` die Herkunft aus `signUp`.
          try {
            const attr = getAttribution();
            if (attr) {
              await supabase
                .from('profiles')
                .update({
                  utm_source: attr.utm_source,
                  utm_medium: attr.utm_medium,
                  utm_campaign: attr.utm_campaign,
                  utm_content: attr.utm_content,
                  utm_term: attr.utm_term,
                  fbclid: attr.fbclid,
                  gclid: attr.gclid,
                  landing_path: attr.landing_path,
                  referrer: attr.referrer,
                })
                .eq('user_id', data.user.id);
            }
          } catch (attrError) {
            console.warn('[Registrieren] Attribution write failed (non-blocking):', attrError);
          }

          // Meta-Pixel: CompleteRegistration (genau einmal pro Session)
          try {
            trackCompleteRegistration({ method: 'email' });
          } catch (pixelError) {
            console.warn('[Registrieren] Pixel CompleteRegistration error:', pixelError);
          }

          // BLOCKING: Show loading screen while we set up the account
          // This prevents the user from seeing "Start" tier flash
          setIsMigrating(true);
          setMigrationStatus("Wir richten deinen Account ein...");
          let detectedTier = 'start';
          let detectedStatus = ''; // 'trialing', 'active', 'manual', etc.

          try {
            // Step 1: Auto-migrate Thinkific data (enrollments, progress, certificates)
            setMigrationStatus("Wir prüfen, ob du bereits Kurse bei uns hattest...");
            console.log('[Registrieren] Starting Thinkific migration...');
            const { data: migrationResult, error: migrationError } = await supabase.functions.invoke('migrate-thinkific-on-register');
            
            if (migrationError) {
              console.warn('[Registrieren] Migration function error:', migrationError);
            } else if (migrationResult?.migrated && migrationResult.enrollments > 0) {
              console.log('[Registrieren] Thinkific migration successful:', migrationResult);
              setMigrationStatus(`${migrationResult.enrollments} Kurs(e) und ${migrationResult.certificates || 0} Zertifikat(e) importiert!`);
              toast.success("Deine Kurse wurden übernommen!", {
                description: `${migrationResult.enrollments} Kurs(e) und ${migrationResult.certificates || 0} Zertifikat(e) von deinem bisherigen Account importiert.`,
                duration: 8000,
              });
            } else {
              console.log('[Registrieren] No Thinkific data to migrate:', migrationResult);
            }

            // Step 2: Pre-fetch subscription status so the correct tier is cached
            setMigrationStatus("Dein Mitgliedsstatus wird geprüft...");
            console.log('[Registrieren] Pre-fetching subscription status...');
            try {
              const { data: subData } = await supabase.functions.invoke('check-abo-subscription');
              if (subData?.subscribed && subData?.tier) {
                detectedTier = subData.tier;
                detectedStatus = subData.status || '';
              }
              console.log('[Registrieren] Detected tier:', detectedTier, 'status:', detectedStatus);
            } catch (subErr) {
              console.warn('[Registrieren] Subscription pre-fetch failed:', subErr);
            }

            // Step 3: Invalidate all caches so dashboard loads fresh data
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: ["student-courses"] }),
              queryClient.invalidateQueries({ queryKey: ["dashboard-courses"] }),
              queryClient.invalidateQueries({ queryKey: ["global-access"] }),
            ]);
          } catch (setupErr) {
            console.warn('[Registrieren] Account setup error (non-blocking):', setupErr);
          } finally {
            setIsMigrating(false);
          }

          // Send welcome email AFTER tier detection - use the CORRECT tier and status
          // Distinguish trial members from direct members (e.g. Thinkific migration)
          const isTrial = detectedStatus === 'trialing';
          // Starter/free users already receive the welcome email via the
          // registration automation (automation-welcome-day0); only fire
          // send-abo-welcome-email for paying tiers (pro/trialing).
          if (detectedTier !== 'start' && detectedTier !== 'free') {
            console.log('[Registrieren] Sending welcome email for tier:', detectedTier, 'isTrial:', isTrial);
            try {
              await supabase.functions.invoke('send-abo-welcome-email', {
                body: {
                  email: email.trim(),
                  tier: detectedTier,
                  firstName: firstName.trim(),
                  lastName: lastName.trim(),
                  isTrial,
                }
              });
            } catch (emailError) {
              console.error('Welcome email error:', emailError);
            }
          }

          // Sync to Brevo list 161 (Basis Mitglieder im Flow) for start-tier users
          if (detectedTier === 'start' || detectedTier === 'free') {
            try {
              await supabase.functions.invoke('sync-brevo-contacts', {
                body: {
                  mode: 'add_contact',
                  listId: 161,
                  email: email.trim(),
                  firstName: firstName.trim(),
                  lastName: lastName.trim(),
                }
              });
              console.log('[Registrieren] Added to Brevo list 161');
            } catch (brevoErr) {
              console.warn('[Registrieren] Brevo sync error (non-blocking):', brevoErr);
            }
          }

          // If user came from a free course CTA, auto-enroll them now
          const pendingCourseId = safeGetItem('pending_free_course_id');
          const pendingCourseSlug = safeGetItem('pending_free_course_slug');

          if (pendingCourseId && pendingCourseSlug) {
            try {
              const { data: enrollData, error: enrollError } = await supabase.functions.invoke('free-course-enroll', {
                body: { course_id: pendingCourseId }
              });

              // Clean up localStorage (success or failure)
              safeRemoveItem('pending_free_course_id');
              safeRemoveItem('pending_free_course_slug');

              if (enrollError) throw enrollError;
              if (enrollData?.error) throw new Error(enrollData.error);

              // Ensure access + enrollments update immediately (avoid needing a manual refresh)
              await queryClient.invalidateQueries({ queryKey: ["global-access"] });
              await queryClient.invalidateQueries({ queryKey: ["course-enrollment"] });
              await queryClient.invalidateQueries({ queryKey: ["course-enrollment-check"] });
              await queryClient.invalidateQueries({ queryKey: ["student-courses"] });
              await queryClient.invalidateQueries({ queryKey: ["dashboard-courses"] });
              await queryClient.refetchQueries({ queryKey: ["global-access"] });

              toast.success("Erfolgreich eingeschrieben!", {
                description: "Du kannst jetzt mit dem Kurs starten.",
              });

              navigate(`/mein-bereich/kurs/${pendingCourseSlug}`);
              return;
            } catch (e: any) {
              console.error('Auto-enrollment failed:', e);
              toast.error("Einschreibung fehlgeschlagen", {
                description: "Bitte klicke erneut auf 'Jetzt kostenlos teilnehmen'.",
              });
            }
          }

          toast.success("Willkommen bei MusiKonzept!");
          navigate('/mein-bereich');
        } else {
          // Email confirmation is required
          // Starter users receive the welcome email via the registration
          // automation (automation-welcome-day0); no extra welcome email here.
          setIsSuccess(true);
        }
      }
    } catch (error: any) {
      toast.error("Ein Fehler ist aufgetreten", {
        description: "Bitte versuche es später erneut.",
      });
    } finally {
      setIsLoading(false);
      setIsRegistrationInProgress(false);
      registrationInProgressRef.current = false;
    }
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isMigrating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30 px-4">
        <div className="w-full max-w-md text-center space-y-6">
          <div className="inline-flex items-center justify-center">
            <img 
              src={musikonzeptLogo} 
              alt="MusiKonzept" 
              className="h-16 w-auto"
            />
          </div>
          <div className="space-y-4">
            <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto" />
            <h2 className="text-xl font-semibold text-foreground">Dein Account wird eingerichtet</h2>
            <p className="text-muted-foreground">
              {migrationStatus || "Wir prüfen, ob du bereits Kurse bei uns hattest..."}
            </p>
            <p className="text-sm text-muted-foreground/70">
              Das kann einen Moment dauern – bitte warte kurz.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30 px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center justify-center">
              <img 
                src={musikonzeptLogo} 
                alt="MusiKonzept" 
                className="h-16 w-auto"
              />
            </Link>
          </div>

          <Card className="border-border/50 shadow-lg">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-xl font-bold mb-2">Bestätige deine E-Mail</h2>
              <p className="text-muted-foreground mb-6">
                Wir haben dir eine E-Mail an <strong>{email}</strong> gesendet. 
                Bitte klicke auf den Link in der E-Mail, um deine Registrierung abzuschließen.
              </p>
              <div className="space-y-3">
              <Link to={`/login${location.search}`}>
                  <Button variant="outline" className="w-full">
                    Zur Anmeldung
                  </Button>
                </Link>
                <Link to="/" className="block text-sm text-muted-foreground hover:text-foreground transition-colors">
                  ← Zurück zur Startseite
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/10" />
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-accent/15 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />
      
      <Dialog open={showAlreadyRegistered} onOpenChange={setShowAlreadyRegistered}>
        <DialogContent className="sm:max-w-md text-center">
          <DialogHeader className="items-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <DialogTitle>E-Mail bereits registriert</DialogTitle>
            <DialogDescription className="text-center">
              Diese E-Mail-Adresse ist bereits registriert.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="rounded-lg bg-secondary/50 border border-secondary p-4 text-sm text-left space-y-1">
              <p className="font-medium text-foreground">🎹 Hattest du einen Account bei Miko Studio oder MusiKonzept?</p>
              <p className="text-muted-foreground text-xs">
                Dann existiert dein Account bereits – melde dich einfach mit deiner bekannten E-Mail-Adresse an. Deine Daten werden automatisch übernommen.
              </p>
            </div>
            <Button className="w-full" onClick={() => navigate('/login')}>
              Zum Login
            </Button>
            <Button variant="ghost" className="w-full" onClick={() => setShowAlreadyRegistered(false)}>
              Andere E-Mail verwenden
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center">
            <img 
              src={musikonzeptLogo} 
              alt="MusiKonzept" 
              className="h-16 w-auto"
            />
          </Link>
        </div>

        {/* Migration hint banner */}
        <div className="mb-4 rounded-xl border border-primary/20 bg-primary/5 backdrop-blur-sm px-4 py-3 flex items-start gap-3">
          <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-foreground">Wir sind umgezogen!</p>
            <p className="text-muted-foreground text-xs mt-0.5">
              Warst du auf unserer alten Homepage bereits registriert? Nutze einfach dieselbe E-Mail-Adresse – deine Kurse und Zertifikate werden automatisch übernommen.
            </p>
          </div>
        </div>

        <Card className="border-border/50 shadow-xl shadow-primary/5">
          <CardHeader className="space-y-1 text-center">
            <CardTitle className="text-2xl font-bold">Kostenlos starten</CardTitle>
            <CardDescription>
              Erstelle deinen Account und entdecke sofort kostenlose Inhalte
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">Vorname</Label>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="Max"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    autoComplete="given-name"
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Nachname</Label>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Mustermann"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    autoComplete="family-name"
                    disabled={isLoading}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail-Adresse</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="deine@email.de"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Passwort</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Sicheres Passwort wählen"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    disabled={isLoading}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {(password.length > 0 || confirmPassword.length > 0) && (
                  <ul className="space-y-1 rounded-md bg-muted/50 p-3 text-sm" aria-live="polite">
                    {passwordChecks.map((check) => {
                      const passed = check.test(password);
                      return (
                        <li
                          key={check.id}
                          className={`flex items-center gap-2 ${
                            passed ? "text-emerald-600" : "text-muted-foreground"
                          }`}
                        >
                          {passed ? (
                            <Check className="h-4 w-4 shrink-0" aria-label="erfüllt" />
                          ) : (
                            <X className="h-4 w-4 shrink-0 text-muted-foreground/60" aria-label="offen" />
                          )}
                          <span>{check.label}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Passwort wiederholen"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    disabled={isLoading}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoading}
                size="lg"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Registrieren...
                  </>
                ) : (
                  "Account erstellen"
                )}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="flex flex-col gap-4 text-center text-sm">
            <p className="text-muted-foreground">
              Bereits registriert?{" "}
              <Link to={`/login${location.search}`} className="text-primary font-medium hover:underline">
                Jetzt anmelden
              </Link>
            </p>
            <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">
              ← Zurück zur Startseite
            </Link>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default Registrieren;
